<template>
  <auth-form
    :val-error="ValError"
    :error-message="ErrorMessage"
    :show-logging-in="showLoggingIn"
    :logging-in="loggingIn"
    action-text="Login"
    link-text="Don’t have an account?"
    link-label="Register here"
    link-to="/register"
    @submit="handleSubmit"
    @button-click="onLogInButton"
    @google-login="handleGoogleLogin"
    @microsoft-login="handleMicrosoftLogin"
    @safenet-login="handleSafeNetLogin"
  >
    <template #title>
      <h2>Welcome back!</h2>
    </template>
    <template #form-fields>
      <label for="username">Username *</label>
      <input
        type="text"
        id="username"
        v-model="username"
        placeholder="Enter your username"
        :class="{ 'is-invalid': submitted && !username }"
      />
      <div v-show="submitted && !username" class="invalid-feedback">
        Username is required
      </div>
      <label for="password">Password *</label>
      <input
        type="password"
        v-model="password"
        id="password"
        placeholder="Enter password"
        :class="{ 'is-invalid': submitted && !password }"
      />
      <div v-show="submitted && !password" class="invalid-feedback">
        Password is required
      </div>
      <div class="remember-forgot">
        <router-link :to="{ name: 'reset_password' }">
          <span>Forgot your password?</span>
        </router-link>
      </div>
    </template>
    <template #button-text>Log In</template>
  </auth-form>
</template>

<script>
import { apiService, sessionService } from "@/_services";
import { getDefaultAnalyticsPreferences } from "@/_core/data/Advanced";
import { getDefaultDataModelPreferences } from "@/_core/data/DataModel";
import { getDefaultHedgePreferences } from "@/_core/data/Overview";
import { getDefaultMarketsPreferences } from "@/_core/data/Markets";
import { getDefaultOptimizerPreferences } from "@/_core/data/Optimizer";
import { getDefaultOverviewPreferences } from "@/_core/data/Overview";
import { mapActions, mapState } from "vuex";
import { jwtDecode } from "jwt-decode";
import { msalService } from "@/_services/msal.service";
import AuthForm from "./AuthForm.vue";
import router from "../../router";

export default {
  components: { AuthForm },
  data() {
    return {
      ErrorMessage: "",
      password: "",
      showLoggingIn: false,
      showModalLoading: false,
      showModalLoadingAux: false,
      submitted: false,
      username: "",
      ValError: false,
      showResetWarning: false,
      // SafeNet configuration
      safenetConfig: {
        clientId: "114a431a-77c2-46a4-85c1-2cb1bc56db03",
        authEndpoint:
          "https://idp.us.safenetid.com/auth/realms/IBYTF6PXMP-STA/protocol/openid-connect/auth",
        redirectUri: "https://www.quanspire.com/oidc/callback/",
        scope: "openid profile email",
      },
    };
  },
  computed: {
    ...mapState("authentication", ["status"]),
    loggingIn() {
      return this.$store.state.authentication.loggingIn;
    },
    ...mapState({
      commodities: (state) => state.commodities.all,
      companies: (state) => state.companies.all,
      countries: (state) => state.countries.all,
      currencies: (state) => state.currencies.all,
      etfs: (state) => state.etfs.all,
      futures: (state) => state.futures.all,
      indexes: (state) => state.indexes.all,
      intRates: (state) => state.intRates.all,
      user_preferences: (state) => state.user_preferences.all,
      user_routes: (state) => state.user_routes.all,
      user: (state) => state.user.all,
    }),
  },
  created() {
    document.title = "Quanspire | Login";
    // Only tear down local session state if there isn't already a valid one.
    // Visiting /login must never silently wipe a valid session that other
    // tabs/windows are relying on (localStorage is shared across tabs).
    if (!this.hasValidExistingSession()) {
      this.$store.dispatch("authentication/logout");
    }
    this.initializeMicrosoftAuth();
    this.handleSafeNetCallback();
  },
  watch: {
    showModalLoading(val) {
      if (val === false) {
        setTimeout(() => {
          this.showModalLoadingAux = false;
        }, 1000);
      } else {
        this.showModalLoadingAux = true;
      }
    },
  },
  methods: {
    hasValidExistingSession() {
      const token = localStorage.getItem("token");
      if (!token) return false;
      try {
        const decoded = jwtDecode(token);
        return Date.now() < decoded.exp * 1000;
      } catch {
        return false;
      }
    },
    initializeMicrosoftAuth() {
      this.msalInstance = msalService.instance;
    },
    async handleMicrosoftLogin() {
      try {
        const loginRequest = {
          scopes: msalService.scopes,
          prompt: "select_account",
        };
        const response = await this.msalInstance.loginPopup(loginRequest);
        this.showLoggingIn = true;
        this.msalInstance.setActiveAccount(response.account);

        await this.$store.dispatch(
          "authentication/microsoftLogin",
          response.idToken,
        );
        await this.fetchUserData();
        router.push("/overview/data-table");
      } catch (error) {
        console.error("Microsoft Login Error:", error);
        this.ErrorMessage = this.mapAuthError(error);
        this.ValError = true;
      } finally {
        this.showLoggingIn = false;
        this.showModalLoading = false;
      }
    },
    async handleSafeNetLogin() {
      try {
        // Generate a state parameter for CSRF protection
        const state = Math.random()
          .toString(36)
          .substring(2);
        // Store state in localStorage for validation in callback
        localStorage.setItem("safenet_state", state);

        // Construct the SafeNet authorization URL
        const authUrl = new URL(this.safenetConfig.authEndpoint);
        authUrl.searchParams.append("response_type", "code");
        authUrl.searchParams.append("client_id", this.safenetConfig.clientId);
        authUrl.searchParams.append(
          "redirect_uri",
          this.safenetConfig.redirectUri,
        );
        authUrl.searchParams.append("scope", this.safenetConfig.scope);
        authUrl.searchParams.append("state", state);

        // Redirect to SafeNet's authorization endpoint
        window.location.href = authUrl.toString();
      } catch (error) {
        console.error("SafeNet Login Error:", error);
        this.ErrorMessage = "Error initiating SafeNet login";
        this.ValError = true;
      }
    },
    async handleSafeNetCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const session_state = urlParams.get("session_state");
      const error = urlParams.get("error");

      if (error) {
        this.ErrorMessage = decodeURIComponent(error);
        this.ValError = true;
        this.showLoggingIn = false;
        router.replace({ path: "/login" });
        return;
      }

      if (code && state) {
        this.showLoggingIn = true;
        this.showModalLoading = true;
        // Strip the one-time code/state from the address bar immediately so a
        // reload or a restored/discarded tab can't replay an already-used code.
        router.replace({ path: "/login" });
        try {
          // Validate state
          //const storedState = localStorage.getItem("safenet_state");
          //if (state !== storedState) {
          //  throw new Error("Invalid state parameter");
          //}

          // Exchange code for tokens
          const response = await apiService.checkCallback(
            code,
            state,
            session_state,
          );
          const { access_token, refresh_token } = response;

          // Commit tokens to store
          await this.$store.commit("authentication/loginSuccess", {
            access: access_token,
            refresh: refresh_token,
            provider: "safenet",
          });
          await sessionService.onLoginSuccess(access_token);

          // Fetch user data
          await this.fetchUserData();
          this.showModalLoading = false;
          router.push("/overview/data-table");
        } catch (err) {
          console.error("SafeNet Callback Error:", err);
          this.ErrorMessage =
            err.response?.data?.error || this.mapAuthError(err);
          this.ValError = true;
        } finally {
          this.showLoggingIn = false;
          this.showModalLoading = false;
          localStorage.removeItem("safenet_state");
        }
      }
    },
    async fetchUserData() {
      await this.getAllUser();
      await Promise.all([
        this.getAllCompanies(),
        this.getAllCurrencies(),
        this.getAllCommodities(),
        this.getAllEtfs(),
        this.getAllIndexes(),
        this.getAllIntRates(),
        this.getAllFutures(),
        this.getAllUserPreferences(this),
        this.getAllUserRoutes(),
        this.getAllCountries(),
      ]);
      await Promise.all([
        getDefaultMarketsPreferences(this),
        getDefaultDataModelPreferences(this),
        getDefaultAnalyticsPreferences(this),
        getDefaultOptimizerPreferences(this),
        getDefaultOverviewPreferences(this),
        getDefaultHedgePreferences(this),
      ]);
    },
    onLogInButton() {
      this.source = "legacy";
    },
    async handleGoogleLogin(response) {
      try {
        await this.$store.dispatch(
          "authentication/googleLogin",
          response.credential,
        );
        this.showLoggingIn = true;
        this.showModalLoading = true;
        await this.fetchUserData();
        router.push("/overview/data-table");
      } catch (error) {
        console.error("Google Login Error:", error);
        this.ErrorMessage = this.mapAuthError(error);
        this.ValError = true;
      } finally {
        this.showLoggingIn = false;
        this.showModalLoading = false;
      }
    },
    async handleSubmit() {
      this.submitted = true;
      const { username, password } = this;
      if (!username || !password) return;

      this.showLoggingIn = true;
      this.ValError = false;
      this.ErrorMessage = "";
      this.showModalLoading = true;
      try {
        await this.login(this);
        await this.fetchUserData();
        router.push("/overview/data-table");
      } catch (err) {
        this.ErrorMessage = this.mapAuthError(err);
        this.ValError = true;
      } finally {
        this.showLoggingIn = false;
        this.showModalLoading = false;
      }
    },
    /**
     * Map any login/error object to a clear, user-facing message.
     */
    mapAuthError(err) {
      if (!err) {
        return "An error occurred during login. Please try again.";
      }
      if (err.message === "callback.newPasswordRequired is not a function") {
        return "Your password has expired, please click Forgot Password button to reset it";
      }
      switch (err.type) {
        case "auth":
          return "Incorrect username or password.";
        case "network":
          return "We couldn't reach the server. Check your connection and try again.";
        case "timeout":
          return "The server took too long to respond. Please try again.";
        case "server":
          return "The server encountered a problem. Please try again later.";
        default:
          return (
            err.message || "An error occurred during login. Please try again."
          );
      }
    },
    ...mapActions("user", { getAllUser: "getAll" }),
    ...mapActions("companies", { getAllCompanies: "getAll" }),
    ...mapActions("currencies", { getAllCurrencies: "getAll" }),
    ...mapActions("commodities", { getAllCommodities: "getAll" }),
    ...mapActions("etfs", { getAllEtfs: "getAll" }),
    ...mapActions("futures", { getAllFutures: "getAll" }),
    ...mapActions("indexes", { getAllIndexes: "getAll" }),
    ...mapActions("intRates", { getAllIntRates: "getAll" }),
    ...mapActions("user_preferences", { getAllUserPreferences: "getAll" }),
    ...mapActions("user_routes", { getAllUserRoutes: "getAll" }),
    ...mapActions("countries", { getAllCountries: "getAll" }),
    ...mapActions("authentication", {
      login: "login",
      googleLogin: "googleLogin",
      microsoftLogin: "microsoftLogin",
      safenetLogin: "safenetLogin", // Add this action
    }),
  },
};
</script>

<style scoped>
.remember-forgot {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 12px;
}

.remember-forgot a {
  color: var(--symmetry-h);
  text-decoration: none;
  transition: color 0.3s ease;
}

.remember-forgot a:hover {
  color: var(--hedge-h);
}
</style>
